import { ButtonType, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogProps, DialogType,
  DialogWidget, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { getAndOpenLinkedTaskDetailsDialog } from '~src/linkedTasks/components/LinkedTaskDetailsDialog';
import { areLinkedTasksEnabled } from '~src/linkedTasks/utils/LinkedTaskHelpers';

import { Messages } from '../../codegen/Localize';
import { ReactModalService } from '../../commonInfra/dialogs/ReactModalService';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { IStringValuedFilterClause } from '../../spacesPlatform/models/IFilterClause';
import { IBackgroundJobInfo } from '../types/BackgroundJobs';
import { GetBackgroundJobExtendedInfoRequest } from '../types/GetBackgroundJobExtendedInfoRequest';
import { IBackgroundJob } from '../types/IBackgroundJob';
import { getBackgroundJobExtendedInfoDialog, IBackgroundJobExtendedInfo } from '../types/IBackgroundJobExtendedInfo';
import { BackgroundJobDefinition } from './contentDefinitions/BackgroundJobDefinition';

export const jobDetailDialogTestId = 'background-job-extended-info-';
export const jobDetailDialogFooterTestId = 'footer-confirm';
export const footerTestId = 'footer';
export const extendedInfoLinkedTaskTestId = 'BackgroundJobExtendedInfoLinkedTaskTextLink';
export interface BackgroundJobExtendedInfoDialogProps {
  backgroundJob: IBackgroundJob;
  data: IBackgroundJobExtendedInfo;
  text: string;
  title: string;
  testId: string;
  isServerPage: boolean;
  close: () => void;
}

export interface BackgroundJobExtendedInfoDialogInstance {
  backgroundJob: IBackgroundJobInfo;
  extendedInfo: IBackgroundJobExtendedInfo;
  close: () => void;
}

// The reference to this immutable array gets overwritten as dialogs are open or closed
let dialogInstances: ReadonlyArray<BackgroundJobExtendedInfoDialogInstance> = [];
export class BackgroundJobExtendedInfoDialog extends React.PureComponent<BackgroundJobExtendedInfoDialogProps> {

  public static toggle(
    backgroundJob: IBackgroundJobInfo
  ) {
    const existingInstance = dialogInstances.find(instance => instance.backgroundJob.jobId === backgroundJob.jobId);
    if (existingInstance) {
      existingInstance.close();
    } else {
      BackgroundJobExtendedInfoDialog.open(backgroundJob);
    }

  }

  private static async open(
    backgroundJob: IBackgroundJobInfo
  ) {
    const request = new GetBackgroundJobExtendedInfoRequest({ backgroundJobId: backgroundJob.jobId + '' });
    const extendedInfo = await WebHttpClientWithErrorToastInstance.sendRequest(request);

    if (extendedInfo.errors && extendedInfo.errors.length > 0) {
      const service = ToasterService.instance;
      service.pop({
        text: Messages.backgroundJobsExtendedInfoError(),
        type: ToastType.Error,
      });

      // Failed to open the dialog: bail
      return;
    }

    const siteIdFilter = BackgroundJobDefinition.defaultFilters('').siteId;
    const isServerPage = (!siteIdFilter) || (siteIdFilter as IStringValuedFilterClause).value === undefined;

    const dialogClose = () => {
      modalInstance.close();
      dialogInstances = dialogInstances.filter(instance => instance.backgroundJob.jobId !== backgroundJob.jobId);
    };

    const modalInstance = ReactModalService.openCustom(({ close }) => (
      <BackgroundJobExtendedInfoDialog
        text={backgroundJob.jobId + ''}
        title={Messages.backgroundJobsExtendedInfoTitle({ ID: backgroundJob.jobId + '' })}
        testId={jobDetailDialogTestId + backgroundJob.jobId}
        isServerPage={isServerPage}
        close={dialogClose}
        backgroundJob={backgroundJob}
        data={extendedInfo}
      />
    ));

    dialogInstances = dialogInstances.concat({
      backgroundJob,
      extendedInfo,
      close: dialogClose,
    });

  }


  constructor(props: BackgroundJobExtendedInfoDialogProps) {
    super(props);
    this.state = {};
  }

  render() {
    const dialogProps: DialogProps = {
      type: DialogType.NormalWithTitleBar,
      title: this.props.title,
      testId: this.props.testId,
      shouldReturnFocusOnClose: false,
      dialogWidth: 375,
      isModal: false,
      showCloseButton: true,
      onCloseRequest: this.props.close,
      positioningBehavior: DialogPositioningBehavior.Draggable,
      showFooter: true,
      footerContent: <DialogFooterButtonsWidget
        buttons={[{
          handleClick: () => {
            this.props.close();
          },
          buttonType: ButtonType.Primary,
          children: Messages.backgroundJobsExtendedInfoOK(),
          testId: jobDetailDialogFooterTestId
        }]}
        testId={footerTestId}
      />,
    };

    const isLinkedTaskJob = areLinkedTasksEnabled() && this.props.data.linkedTaskId !== undefined &&
      this.props.data.linkedTaskStepNumber !== undefined && this.props.data.linkedTaskTotalSteps !== undefined;
    const linkedTaskProps = {
      linkedTaskId: this.props.data.linkedTaskId,
      linkedTaskStepNumber: this.props.data.linkedTaskStepNumber,
      linkedTaskTotalSteps: this.props.data.linkedTaskTotalSteps,
    };
    function handleLinkedTaskClick() {
      getAndOpenLinkedTaskDetailsDialog(linkedTaskProps.linkedTaskId);
    }
    const contentName = Messages.backgroundJobsExtendedInfoContent({
      CONTENT_NAME: this.props.data.contentName,
    });

    const flowId = this.props.data.flowId;
    const flowName = this.props.data.flowName;
    const isFlowRunJob = this.props.data.flowId && this.props.data.flowName;

    const generateDialogContent = (i: number, content: React.ReactNode):
    React.ReactNode => {
      return (
        <p
          key={'background-job-extended-info-dialog-line-' + i}
          style={{margin: '0'}}
        >
          {content}
        </p>
      );
    };

    const dialogs = [];
    const lines = getBackgroundJobExtendedInfoDialog(this.props.backgroundJob, this.props.data, this.props.isServerPage);
    for (let i =0; i<lines.length; i++){
      const line = lines[i];
      let dialogContent = null;
      if (line === contentName && isLinkedTaskJob) {
        const linkedTaskDialogContent = (
          <>
            {Messages.backgroundJobsExtendedInfoContentLinkedTask()}
            <TextLinkWidget
            testId={extendedInfoLinkedTaskTestId}
            onClick={handleLinkedTaskClick}
            >
            {this.props.data.contentName}
            </TextLinkWidget>
            {Messages.backgroundJobsExtendedInfoLinkedTaskSteps({
            LINKEDTASK_STEP_NUM: linkedTaskProps.linkedTaskStepNumber,
            LINKEDTASK_TOTAL_STEPS: linkedTaskProps.linkedTaskTotalSteps,
            })}
          </>
        );
        dialogContent = generateDialogContent(i, linkedTaskDialogContent);
      } else {
        if (FeatureFlags.flowContentLinkToJobPageEnabled){
          if (line === flowId) {
            if (isFlowRunJob){
              // Get site-aware flow URL path, fallback to default if no site context
              const siteUrls = siteUrlsSelector(getAppStore().getState());
              const flowPath = siteUrls ? siteUrls.flowUrl(flowId) : `/#/flows/${flowId}`;
              const flowHistoryUrl = `${window.location.origin}${flowPath}/overview`;
              const flowContentLinkToJobPageDialogContent = (
                <>
                  Content:
                  <TextLinkWidget
                  href={flowHistoryUrl}
                  kind={TextLinkKind.Standard}
                  target='_blank'
                  testId='standard'
                  >{flowName}
                  </TextLinkWidget>
                </>
              );
              dialogContent = generateDialogContent(i, flowContentLinkToJobPageDialogContent);
            } else {
              continue;
            }
          } else if(isFlowRunJob && (line === contentName || line === flowName)){
            continue;
          } else {
            dialogContent = generateDialogContent(i, line);
          }
        } else {
          if (line === flowId || line === flowName){
            continue;
          } else {
            dialogContent = generateDialogContent(i, line);
          }
        }
      }
      if (dialogContent) {dialogs.push(dialogContent);}
    }
    return <DialogWidget {...dialogProps}>
      {dialogs}
    </DialogWidget>;
  }
}